import * as React from 'react'
import GoogleMapReact from 'google-map-react'
import MapMarker from './components/MapMarker'
import { Tooltip } from '@mui/material'
import { GpsFixedIcon } from '../icons/GpsFixedIcon'
import { GpsOffIcon } from '../icons/GpsOffIcon'
import { useMapGeometryEditorLogic } from './hooks/useMapGeometryEditorLogic'
import { MapGeometry, MapsApi } from './interfaces'
import { geometryHasPoints, updateMapBoundaries, getGeometriesBoundPoints } from './maps-utils'
import { GoogleMapsGeometryManager } from './google-maps-geometry-manager'
import { styled } from '../../theme/styled'
import { TopActions } from './components/TopActions'
import { MapViewProps } from './MapViewProps'

const DEFAULT_CENTER_COORDS_CALIFORNIA = { lat: 37.7749, lng: -122.4194 }

const MapView = ({
  properties,
  highlightPropertyId,
  isExternallyHidden,
  onPropertyHover,
  onPropertyClick,
  style,
  defaultCenter,
  geometries,
  onClearGeometries,
  onGeometryCreate,
  draft,
  onDraftChanged,
  onPopoverClick,
  onGoogleApiLoaded,
  hasGeolocationButton,
  onGeolocationButtonClick,
  customActions,
  ...rest
}: MapViewProps) => {
  const { onMapClick, setEditorShape, onCancel, onConfirm } = useMapGeometryEditorLogic({
    onGeometryConfirmed: onGeometryCreate,
    onDraftChanged,
    draft: draft || { shape: 'none', key: 'null' }
  })
  const [activeMarkerId, setActiveMarkerId] = React.useState<string | null>(null)
  const [browserGeolocation, setBrowserGeolocation] = React.useState<{
    lat: number
    lng: number
  }>()

  const propertiesVersion = React.useRef({ n: 0, properties })
  if (propertiesVersion.current.properties !== properties) {
    propertiesVersion.current.n++
  }

  //one statefull manager per map
  const manager = React.useMemo(() => new GoogleMapsGeometryManager(), [])
  manager.setOnGeometryUpdated((geometry) => {
    onDraftChanged?.({ geometry, action: 'handle' })
  })

  const mapsApiRef = React.useRef<MapsApi>()
  const subjectProperty = React.useMemo(() => {
    return properties.find((x) => x.subjectProperty)
  }, [properties])
  const currentLocationCoods = isExternallyHidden &&
    hasGeolocationButton &&
    browserGeolocation && { lat: browserGeolocation.lat, lng: browserGeolocation.lng }
  const defaultCenters = React.useRef(
    subjectProperty || defaultCenter || currentLocationCoods || DEFAULT_CENTER_COORDS_CALIFORNIA
  )

  const moveMapTo = React.useCallback(
    (coords: { lat: number; lng: number }) => {
      mapsApiRef.current && mapsApiRef.current?.map.panTo(coords)
    },
    [mapsApiRef.current]
  )

  if (hasGeolocationButton && !browserGeolocation) {
    navigator.geolocation?.getCurrentPosition((pos) => {
      const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude }
      setBrowserGeolocation(coords)
      isExternallyHidden &&
        setTimeout(() => {
          moveMapTo({ ...coords })
        }, 200)
    })
  }

  React.useEffect(() => {
    hasGeolocationButton &&
      browserGeolocation &&
      mapsApiRef.current &&
      isExternallyHidden &&
      setTimeout(() => {
        moveMapTo({ ...browserGeolocation })
      }, 200)
  }, [hasGeolocationButton, browserGeolocation, mapsApiRef.current, isExternallyHidden, moveMapTo])

  React.useEffect(() => {
    setTimeout(() => {
      if (mapsApiRef.current && !isExternallyHidden) {
        updateMapBoundaries(mapsApiRef.current, [
          ...properties,
          ...getGeometriesBoundPoints(geometries)
        ])
      }
    }, 100)
  }, [isExternallyHidden, properties, geometries])

  const draftGeometry: MapGeometry | null = React.useMemo(() => {
    if (draft?.shape === 'polygon') {
      return { shape: 'polygon', points: draft.points, key: 'draft' }
    }
    if (draft?.shape === 'circle') {
      const { center, radius } = draft
      if (center && radius) {
        return { shape: 'circle', center, radius, key: 'draft' }
      }
    }
    return null
  }, [draft])

  const helperDots = React.useMemo(() => {
    if (draft?.shape === 'circle') {
      const { center, radius } = draft
      if (center && !radius) {
        return [<GoogleMapsLikeDot key={`dot-center`} {...center} />]
      }
    } else if (draft?.shape === 'polygon') {
      const { points = [] } = draft
      if (points.length > 0)
        return [
          <FirstDot key={`first-dot`} {...points[0]} />,
          <LastDot key={`dot-last`} {...points.slice(-1)[0]} />
        ]
    }
    return []
  }, [draft])

  React.useEffect(() => {
    if (manager.hasMap())
      manager.updateGeometries([...geometries, ...(draftGeometry ? [draftGeometry] : [])])
  }, [manager, geometries, draftGeometry])

  const highlightedProperty = properties.find((p) => p.id === highlightPropertyId)

  const usedPropertyKeys = new Map<string, number>()
  function getSafePropertyKey(key: string) {
    const index = usedPropertyKeys.get(key) || 0
    usedPropertyKeys.set(key, index + 1)
    return `${key}-${index}`
  }

  const handlePropertyClick = (id: string) => {
    const pureId = id.split('-').slice(0, -1).join('-')
    setActiveMarkerId(pureId)
    onPropertyClick?.(id)
  }

  const mapItems = isExternallyHidden
    ? []
    : [
        properties.map((x) => {
          return (
            <MapMarker
              key={getSafePropertyKey(x.id)}
              lat={x.lat}
              lng={x.lng}
              highlight={highlightPropertyId === x.id}
              isActive={activeMarkerId === x.id}
              propertyData={x.propertyData}
              color={x.color}
              isSubjectProperty={x.subjectProperty}
              onPopoverClick={onPopoverClick}
            />
          )
        }),
        ...helperDots
      ]

  const draftHasPoints = geometryHasPoints(draft)
  const handleApplyClick = onConfirm
  const handleCancelClick = draftHasPoints && onCancel ? onCancel : undefined
  const handleClearClick = geometries.length && !draftHasPoints ? onClearGeometries : undefined
  const handleDrawClick = setEditorShape ? () => setEditorShape('polygon') : undefined
  const handleRadiusClick = setEditorShape ? () => setEditorShape('circle') : undefined
  const handleGpsButtonClick = () => {
    if (browserGeolocation) {
      mapsApiRef.current?.map.panTo({ ...browserGeolocation })
      onGeolocationButtonClick?.(browserGeolocation)
    }
  }
  const shapeEditor = (draft?.shape?.replace('collecting-', '') || null) as any
  const isDrawing = shapeEditor && shapeEditor !== 'none'

  const actions = React.useMemo(() => {
    const defaultActions = customActions || []
    return hasGeolocationButton
      ? [
          {
            id: 'geolocation-button',
            Icon: () => (browserGeolocation ? <GpsFixedIcon /> : <GpsOffIcon />),
            onClick: handleGpsButtonClick,
            tooltip: !browserGeolocation
              ? 'Please allow Location access to move the map to you location'
              : ''
          },
          ...defaultActions
        ]
      : defaultActions
  }, [hasGeolocationButton, browserGeolocation])

  return (
    <React.Fragment>
      {onDraftChanged && (
        <TopActions
          shapeEditor={shapeEditor}
          onApplyClick={handleApplyClick}
          onCancelClick={handleCancelClick}
          onClearClick={handleClearClick}
          onDrawClick={handleDrawClick}
          onRadiusClick={handleRadiusClick}
        />
      )}

      {actions.map(({ Icon, ...action }, index) => (
        <Tooltip title={action.tooltip || ''} placement='left' key={action.id}>
          <CustomActionButton
            $position={index}
            onClick={() => {
              action.onClick({
                moveMapTo,
                onClearGeometries,
                onGeometryCreate,
                onDraftChanged,
                center: defaultCenters.current
              })
            }}
            id={action.id}
          >
            <Icon />
          </CustomActionButton>
        </Tooltip>
      ))}

      <GoogleMapReact
        hoverDistance={draft && draft.shape !== 'none' ? 0 : 24}
        options={{
          clickableIcons: false,
          fullscreenControl: false,
          controlSize: 36,
          draggableCursor: isDrawing ? 'pointer' : 'grab'
        }}
        onChildClick={(e) => handlePropertyClick(e)}
        onChildMouseEnter={(e) => onPropertyHover?.(e)}
        onClick={(e) => {
          onMapClick?.(e)
          setActiveMarkerId(null)
        }}
        onGoogleApiLoaded={(api) => {
          onGoogleApiLoaded?.(api)
          mapsApiRef.current = api
          manager.setMap(api.map)
          manager.updateGeometries(geometries)
          if (!isExternallyHidden)
            updateMapBoundaries(api, [...properties, ...getGeometriesBoundPoints(geometries)])
        }}
        center={
          highlightedProperty
            ? {
                lat: highlightedProperty.lat,
                lng: highlightedProperty.lng
              }
            : undefined
        }
        yesIWantToUseGoogleMapApiInternals
        defaultCenter={defaultCenters.current}
        defaultZoom={11}
        style={{
          ...style,
          width: '100%'
        }}
        {...rest}
      >
        {mapItems}
      </GoogleMapReact>
    </React.Fragment>
  )
}

export default MapView

const GoogleMapsLikeDot = styled.div`
  position: absolute;
  border-radius: 50%;
  width: 9px;
  height: 9px;
  margin-top: -5px;
  margin-left: -5px;
  border: solid 1px red;
  pointer-events: none;
  background: white;
`

const LastDot = styled.div`
  position: absolute;
  z-index: 2;
  border-radius: 50%;
  width: 17px;
  height: 17px;
  margin-top: -9px;
  margin-left: -9px;
  border: solid 1px red;
  pointer-events: none;
`

const FirstDot = styled.div`
  position: absolute;
  z-index: 2;
  border-radius: 50%;
  width: 3px;
  height: 3px;
  margin-top: -1px;
  margin-left: -1px;
  background: red;
  pointer-events: none;
`

const CustomActionButton = styled.div<{ $position: number }>`
  position: absolute;

  z-index: 3;
  bottom: ${(p) => (5.75 + p.$position * 2.5)}rem;
  right: 1px;
  border: 0;
  padding: 0;
  margin: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
  border-radius: 2px;
  cursor: pointer;
  background-color: rgb(255, 255, 255);

  text-transform: none;
  appearance: none;
  cursor: pointer;
  overflow: hidden;
  width: 36px;
  height: 36px;
  color: #666;
  &:hover {
    color: #000;
  }
`
